import qs from "qs-lite";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import api from "../../utils/api";
import { invalidate } from "../../helpers/clientHelper";
import { useHubSnackBar } from "../useSnackBar";
import {
  MODE_DAILY,
  MODE_SCHEDULER_GRAPH,
  MODE_SCHEDULER_TIMELINE,
  MODE_THROUGHPUT,
  resolveRange,
} from "../../pages/SchedulePage/dateHelper";

const SCHEDULE = "/schedule";

const useResolvedRange = (viewMode) => {
  const [searchParams] = useSearchParams();
  return resolveRange(searchParams, viewMode);
};

const useResolvedLocation = (defaultLocation) => {
  const [searchParams] = useSearchParams();
  return searchParams.get("location") || defaultLocation;
};

const getScheduleOrders = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${SCHEDULE}?${queryString}`);
};
export const useGetScheduleOrders = ({ defaultLocation }) => {
  const location = useResolvedLocation(defaultLocation);
  const { startDate, endDate } = useResolvedRange(MODE_SCHEDULER_TIMELINE);

  const queryProps = { location, startDate, endDate };

  return useQuery([SCHEDULE, { ...queryProps }], getScheduleOrders, {
    retry: false,
  });
};

const SCHEDULE_CHART = `${SCHEDULE}/chart`;

const getScheduleChart = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${SCHEDULE_CHART}?${queryString}`);
};
export const useGetScheduleChart = ({ defaultLocation }) => {
  const location = useResolvedLocation(defaultLocation);
  const { startDate, endDate } = useResolvedRange(MODE_SCHEDULER_GRAPH);

  return useQuery(
    [SCHEDULE_CHART, { location, startDate, endDate }],
    getScheduleChart,
  );
};

const SCHEDULE_THROUGHPUT = `${SCHEDULE}/throughput`;

const getScheduleThroughput = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${SCHEDULE_THROUGHPUT}?${queryString}`);
};

export const useGetScheduleThroughput = ({
  defaultLocation,
  mode = "start",
}) => {
  const location = useResolvedLocation(defaultLocation);
  const { startDate, endDate } = useResolvedRange(MODE_THROUGHPUT);

  return useQuery(
    [SCHEDULE_THROUGHPUT, { location, startDate, endDate, mode }],
    getScheduleThroughput,
  );
};

const SCHEDULE_THROUGHPUT_DAILY = `${SCHEDULE_THROUGHPUT}/daily`;

const getScheduleDailyThroughput = ({ queryKey }) => {
  const queryString = qs.stringify(queryKey[1]);
  return api.get(`${SCHEDULE_THROUGHPUT_DAILY}?${queryString}`);
};

export const useGetScheduleDailyThroughput = ({ defaultLocation }) => {
  const location = useResolvedLocation(defaultLocation);
  const { startDate, endDate } = useResolvedRange(MODE_DAILY);

  return useQuery(
    [SCHEDULE_THROUGHPUT_DAILY, { location, startDate, endDate }],
    getScheduleDailyThroughput,
    { staleTime: 60_000 },
  );
};

const getThroughputCustomer = ({ queryKey }) => {
  const customerId = queryKey[2];
  const queryString = qs.stringify(queryKey[3]);
  return api.get(
    `${SCHEDULE_THROUGHPUT}/customer/${customerId}?${queryString}`,
  );
};
export const useGetThroughputCustomer = ({
  defaultLocation,
  customerId,
  mode = "complete",
  weekStart,
  weekEnd,
  open,
}) => {
  const location = useResolvedLocation(defaultLocation);
  const { startDate: rangeStart, endDate: rangeEnd } =
    useResolvedRange(MODE_THROUGHPUT);

  const startDate = weekStart || rangeStart;
  const endDate = weekEnd || rangeEnd;

  return useQuery(
    [
      SCHEDULE_THROUGHPUT,
      "customer",
      customerId,
      { location, startDate, endDate, mode },
    ],
    getThroughputCustomer,
    { enabled: !!customerId && !!open },
  );
};

const putRescheduleWorkOrder = ({ id, startDate }) =>
  api.put(`${SCHEDULE}/${id}`, { startDate });

export const usePutRescheduleWorkOrder = () => {
  const { addSnackbarSuccess, addSnackbarError } = useHubSnackBar();
  const queryClient = useQueryClient();

  return useMutation(putRescheduleWorkOrder, {
    onMutate: async ({ id, startDate, toId, toIndex }) => {
      await queryClient.cancelQueries(SCHEDULE_CHART);
      const chartQueries = queryClient.getQueriesData(SCHEDULE_CHART);
      const previous = chartQueries.map(([key, data]) => [key, data]);

      chartQueries.forEach(([key, weeks]) => {
        if (!Array.isArray(weeks)) return;
        const sourceWeek = weeks.find((w) =>
          w.elements.some((e) => e.workOrder === id),
        );
        const movedEl = sourceWeek?.elements.find((e) => e.workOrder === id);
        if (!movedEl) return;

        const updated = weeks.map((w) => ({
          ...w,
          elements: w.elements.filter((e) => e.workOrder !== id),
        }));
        const targetWeek = updated.find((w) => String(w.id) === toId);
        if (targetWeek) {
          targetWeek.elements.splice(toIndex, 0, { ...movedEl, startDate });
        }
        queryClient.setQueryData(key, updated);
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      addSnackbarError({ message: "Failed to reschedule work order" });
    },
    onSuccess: () => {
      addSnackbarSuccess({ message: "Successfully Rescheduled Work Order" });
    },
    onSettled: () => {
      invalidate(queryClient, SCHEDULE);
      invalidate(queryClient, SCHEDULE_CHART);
    },
  });
};
